import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IoMdMic } from 'react-icons/io';
import { SpeechLang, SpeechLangOption } from 'hooks/useSpeechInput';
import './VoiceOverlay.css';

// Bell-curve envelope: centre bars taller than edges so the waveform reads
// as a natural speech-amplitude silhouette. SVG user units (viewBox height 64).
const BAR_HEIGHTS = [
  16, 26, 38, 50, 56, 60, 58, 54, 50, 54, 60, 62,
  62, 60, 54, 50, 54, 58, 60, 56, 50, 38, 26, 16,
];

const VIEWBOX_H = 64;
const BAR_W = 8;
const BAR_GAP = 4; // gap between bars
const BAR_STRIDE = BAR_W + BAR_GAP;

interface VoiceOverlayProps {
  lang: SpeechLang;
  langOptions: readonly SpeechLangOption[];
  onStop: () => void;
}

/**
 * Full-screen overlay displayed while voice input is active.
 * Portalled to <body> so it sits above modals and the app shell.
 *
 * Interaction:
 * - Click anywhere → stop recording.
 * - Escape → stop recording (captured before AntD modal handlers).
 *
 * The waveform is decorative: the Web Speech API does not expose raw audio
 * data, so the bars animate with pre-computed CSS delays that approximate
 * the look of a speech waveform.
 */
const VoiceOverlay: React.FC<VoiceOverlayProps> = ({
  lang,
  langOptions,
  onStop,
}) => {
  const onStopRef = useRef(onStop);
  onStopRef.current = onStop;

  // Capture Escape in the capture phase so it stops recording rather than
  // closing whichever AntD modal or panel the user may have open behind.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        onStopRef.current();
      }
    };
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () =>
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, []);

  const currentLangLabel =
    langOptions.find((o) => o.value === lang)?.label ?? lang;

  const viewBoxW = BAR_HEIGHTS.length * BAR_STRIDE - BAR_GAP;

  return createPortal(
    <div
      role="dialog"
      aria-label="Voice input active — click or press Escape to stop"
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-8 bg-black/75 backdrop-blur-sm"
      onClick={onStop}
    >
      {/* Pulsing mic icon */}
      <div className="relative flex items-center justify-center">
        <span className="absolute h-24 w-24 animate-ping rounded-full bg-danger opacity-20" />
        <span className="relative z-10 text-7xl leading-none text-danger">
          <IoMdMic />
        </span>
      </div>

      {/* Status text */}
      <div className="flex flex-col items-center gap-2 text-center">
        <p className="text-3xl font-bold text-white">Listening…</p>
        <p className="text-sm text-white/60">{currentLangLabel}</p>
      </div>

      {/* Animated waveform bars — styled via .voice-bar in App.css */}
      <svg
        viewBox={`0 0 ${viewBoxW} ${VIEWBOX_H}`}
        className="w-72"
        aria-hidden
      >
        {BAR_HEIGHTS.map((h, i) => (
          <rect
            key={i}
            className="voice-bar"
            x={i * BAR_STRIDE}
            y={VIEWBOX_H - h}
            width={BAR_W}
            height={h}
            rx={BAR_W / 2}
            fill="white"
            fillOpacity={0.85}
          />
        ))}
      </svg>

      {/* Behaviour hints + cancel */}
      <div className="flex flex-col items-center gap-1 text-center">
        <p className="text-sm text-white/70">
          Stops automatically when you pause speaking.
        </p>
        <p className="text-sm text-white/70">
          One phrase per session — click mic again to continue.
        </p>
        <p className="mt-2 text-xs text-white/35">
          Click anywhere or press Esc to cancel
        </p>
      </div>
    </div>,
    document.body
  );
};

export default VoiceOverlay;
